import { GroupSecurity } from '@quromedical/fhir-common';
import {
  MeasurementName,
  type WebsocketAlert,
  type WebsocketAlertType,
} from '@quromedical/models';
import type { PatientRouteParams } from 'app/routes';
import { Col } from 'components/base';
import { WithGroup } from 'core/auth';
import { RealtimeAlertsList } from 'core/vitals';
import { Spacer } from 'design-system';
import { Grid } from 'design-system/layout';
import { MARGIN } from 'design-system/theme';
import { useLocalSearchParams } from 'expo-router';
import { useUserSession } from 'hooks';
import { useResponsive } from 'hooks/useResponsive';
import { usePatientVitals } from 'providers/PatientVitalsProvider';
import type React from 'react';
import { useCallback, useState } from 'react';
import { useWindowDimensions } from 'react-native';

import { VitalsGridItem } from './VitalsGridItem';

interface VitalsActiveGridProps {
  scrollToTop?: () => void;
  measurements: MeasurementName[];
}

const alertVitalsFocus: Record<WebsocketAlertType, MeasurementName> = {
  'ecg-sinus-rhythm': MeasurementName.HeartRate,
};

export const VitalsActiveGrid: React.FC<VitalsActiveGridProps> = ({
  scrollToTop,
  measurements,
}) => {
  const session = useUserSession();

  // only users who are allowed to manage device use statements should care about filters
  const canViewFilter = session.hasAny([
    GroupSecurity.DeviceUseStatementCreate,
    GroupSecurity.DeviceUseStatementRead,
  ]);

  const { width } = useWindowDimensions();
  const [expanded, setExpanded] = useState<MeasurementName | undefined>();

  const toggleExpanded = useCallback(
    (measureName: MeasurementName) => {
      if (measureName === expanded) {
        setExpanded(undefined);
      } else {
        setExpanded(measureName);
      }

      if (scrollToTop) {
        scrollToTop();
      }
    },
    [expanded, scrollToTop],
  );

  const columns = useResponsive({ md: 2 }, 1);
  const initialWidth = width / columns;

  const { patientId } = useLocalSearchParams<PatientRouteParams>();
  const { timeFrame, vitals, latest, onRefresh, setTimes, times } =
    usePatientVitals();

  const filteredMeasures = measurements.filter((key) => key !== expanded);

  const [activeAlert, setActiveAlert] = useState<WebsocketAlert>();

  const onAlertPress = useCallback(
    (alert: WebsocketAlert) => {
      setActiveAlert(alert);

      if (!times.duration) {
        return;
      }

      const centerPoint = alert.ts;
      const offset = times.duration / 2;
      const start = centerPoint - offset;

      setTimes({
        duration: times.duration,
        start: new Date(start),
      });

      setExpanded(alertVitalsFocus[alert.type]);
      scrollToTop?.();
    },
    [times.duration, setTimes, scrollToTop],
  );

  const alertKey = activeAlert ? `${activeAlert?.type}-${activeAlert.ts}` : '';

  return (
    <>
      {expanded ? (
        <>
          <VitalsGridItem
            key={alertKey}
            revalidate={onRefresh}
            patientId={patientId}
            measureName={expanded}
            primaryGraphMeasureName={expanded}
            vitals={vitals[expanded]}
            latest={latest[expanded]}
            initialWidth={width}
            timeFrame={timeFrame}
            isExpanded={true}
            onExpandToggled={toggleExpanded}
            canViewFilter={canViewFilter}
          />
          <Spacer height={MARGIN.m} />
        </>
      ) : null}

      <Grid sm={1} md={2} gap="m">
        {filteredMeasures.map((key) => (
          <VitalsGridItem
            revalidate={onRefresh}
            patientId={patientId}
            key={key}
            measureName={key}
            primaryGraphMeasureName={key}
            vitals={vitals[key]}
            latest={latest[key]}
            initialWidth={initialWidth}
            timeFrame={timeFrame}
            isExpanded={false}
            onExpandToggled={toggleExpanded}
            canViewFilter={canViewFilter}
          />
        ))}
        <WithGroup groups={[GroupSecurity.AlertEWS]}>
          <Col>
            <RealtimeAlertsList patientId={patientId} onPress={onAlertPress} />
          </Col>
        </WithGroup>
      </Grid>
    </>
  );
};
